import { showErrorNotification } from '../utils/utils';
import { transactionAxios } from './schedulingApis';

export const S_CURVE_METRICS = ['QUANTITY', 'SCHEDULE_PERCENT'] as const;
export const S_CURVE_GRANULARITIES = ['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR'] as const;

export type SCurveMetric = (typeof S_CURVE_METRICS)[number];
export type SCurveGranularity = (typeof S_CURVE_GRANULARITIES)[number];

export interface SCurvePoint {
  periodStart: string;
  periodLabel: string;
  plannedPeriod?: number | null;
  actualPeriod?: number | null;
  plannedCumulative?: number | null;
  actualCumulative?: number | null;
  plannedCumulativePercent: number | null;
  actualCumulativePercent: number | null;
}

export interface SCurveActualMethod {
  quantity?: number;
  actualSpan?: number;
  estimatedFromCurrentPercent?: number;
}

export interface SCurveNotes {
  plannedSource?: string;
  actualMethod?: SCurveActualMethod;
  linearPlanDates?: unknown;
}

export interface SCurveData {
  projectId: string;
  metric: SCurveMetric | string;
  granularity: SCurveGranularity | string;
  unit: string;
  from: string;
  to: string;
  asOf: string;
  points: SCurvePoint[];
  notes?: SCurveNotes;
}

export interface FetchSCurveParams {
  projectId: string;
  metric: SCurveMetric;
  granularity: SCurveGranularity;
}

function unwrapSCurve(body: unknown): SCurveData {
  if (!body || typeof body !== 'object') {
    throw new Error('S-curve response was empty.');
  }
  const record = body as { data?: unknown; message?: string; points?: unknown };
  const payload = Array.isArray(record.points) ? record : record.data;
  if (!payload || typeof payload !== 'object' || !Array.isArray((payload as SCurveData).points)) {
    const message = typeof record.message === 'string' ? record.message : 'S-curve response did not include points.';
    throw new Error(message);
  }
  return payload as SCurveData;
}

/**
 * GET /dpr/apis/planning/s-curve.
 * Omits from/to so the API uses its default window.
 */
export async function fetchSCurve(params: FetchSCurveParams): Promise<SCurveData> {
  const query = new URLSearchParams({
    projectId: params.projectId,
    metric: params.metric,
    granularity: params.granularity,
  });
  try {
    const res = await transactionAxios.get(`dpr/apis/planning/s-curve?${query.toString()}`);
    return unwrapSCurve(res.data);
  } catch (err) {
    console.error(`Error response: ${err}`);
    showErrorNotification(err, 'Sorry! Failed to fetch the S-curve.');
    throw err;
  }
}
